"use client";

import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  buildHierarchy,
  fmtRel,
  momColor,
  NEW_ENTRANT_COLOR,
  totalFor,
} from "./aggregate";
import type { Mode } from "./aggregate";
import { downloadTreemapImage } from "./download-image";
import {
  computeStackedLayout,
  computeUnifiedLayout,
  fitCatHeader,
  fitHorizontalName,
  fitLeafLabel,
  fitRotatedName,
  PAD_TOP,
} from "./layout";
import type { PositionedLeaf, TreemapLayout } from "./layout";
import { WfLogo } from "./logo";
import { treemapDataSchema } from "./types";
import type { AppLeaf, Selection, TreemapData } from "./types";

import stylesModule from "./treemap.module.css";

const DEFAULT_DATA_URL = "/assets-next/ecommerce-stack/treemap_data.json";

// Concrete key type so dot access is allowed under
// noPropertyAccessFromIndexSignature (CSS modules type to a string index sig).
const styles = stylesModule as unknown as Record<
  | "root"
  | "title"
  | "exporting"
  | "sub"
  | "controls"
  | "ctrl"
  | "ctrlLabel"
  | "select"
  | "seg"
  | "active"
  | "reset"
  | "legend"
  | "lgLayer"
  | "chip"
  | "sw"
  | "warn"
  | "wrap"
  | "svg"
  | "stackRow"
  | "stackHeader"
  | "stackSwatch"
  | "tip"
  | "tipName"
  | "tipCat"
  | "tipRow"
  | "tipK"
  | "pos"
  | "neg"
  | "zero"
  | "footer"
  | "left"
  | "callout"
  | "note"
  | "dlPhoto"
  | "logo"
  | "leaf"
  | "leafRect"
  | "catLabel"
  | "leafText",
  string
>;

const GMV_OPTS: [string, string][] = [
  ["lt1m", "Under $1M"],
  ["1to5m", "$1M – $5M"],
  ["5mplus", "$5M+"],
];
const GROWTH_OPTS: [string, string][] = [
  ["top25", "Top 25% fastest growing brands"],
];

const STACK_BREAKPOINT = 640;

function chooseUnifiedHeight(width: number): number {
  return Math.round(width >= 900 ? width * 0.6 : width * 0.78);
}

interface TooltipState {
  leaf: AppLeaf;
  category: string;
}

/**
 * The eCommerce Stack treemap, rendered natively in React via d3-hierarchy.
 * Laying the treemap out at the true container width (rather than scaling a
 * fixed 1240px canvas down) keeps labels readable at every size; below
 * `STACK_BREAKPOINT` the categories stack vertically so mobile text stays large.
 *
 * `compact` is a hover-only teaser (no controls/legend/footer) for the /data
 * hub; `animate` plays the right-to-left entrance once on first render.
 */
export function EcommerceStackTreemap({
  compact = false,
  animate = false,
  dataUrl = DEFAULT_DATA_URL,
}: {
  compact?: boolean;
  animate?: boolean;
  /** Override the data file — used to pin past articles to their month's snapshot. */
  dataUrl?: string;
}) {
  const uid = useId().replace(/:/g, "");
  const cardRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const animPlayedRef = useRef(false);

  const [data, setData] = useState<TreemapData | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [width, setWidth] = useState(compact ? 900 : 1240);
  const [mode, setMode] = useState<Mode>("cat");
  const [sel, setSel] = useState<Selection>({
    gmv: "all",
    growth: "all",
    ind: "all",
  });
  const [tip, setTip] = useState<TooltipState | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [animTimedOut, setAnimTimedOut] = useState(false);

  // Fetch the (static, CDN-cached) data file client-side — it is ~450KB, too
  // large to inline into the page payload, and is needed for interactive
  // filtering anyway.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(dataUrl);
        const raw = await r.json();
        if (alive) setData(treemapDataSchema.parse(raw));
      } catch {
        if (alive) setLoadError(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [dataUrl]);

  // Measure the real container width so the treemap lays out at 1:1 pixels.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.round(el.clientWidth));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!window.matchMedia) return;
    setReduceMotion(
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }, []);

  // Play the entrance animation once, on the first render that actually has
  // data (the data file loads asynchronously after mount, so we can't gate on
  // "first render" — the cells don't exist yet then).
  useEffect(() => {
    if (!animate || !data || animPlayedRef.current) return;
    animPlayedRef.current = true;
    // Safety net: if the animation clock never advances (e.g. a backgrounded
    // tab) drop the entrance animation so cells can't stay stuck hidden.
    const t = setTimeout(() => setAnimTimedOut(true), 2500);
    return () => clearTimeout(t);
  }, [animate, data]);

  const stacked = !compact && width < STACK_BREAKPOINT;

  const { total, cats } = useMemo(
    () => (data ? buildHierarchy(data, sel) : { total: 0, cats: [] }),
    [data, sel]
  );

  const layout: TreemapLayout | null = useMemo(() => {
    if (!cats.length || width <= 0) return null;
    return stacked
      ? computeStackedLayout(cats, width)
      : computeUnifiedLayout(cats, width, chooseUnifiedHeight(width));
  }, [cats, width, stacked]);

  const belowSample = !!data && total > 0 && total < data.minSample;
  const doAnimate =
    animate &&
    !!data &&
    !animPlayedRef.current &&
    !reduceMotion &&
    !animTimedOut;

  // --- tooltip ---------------------------------------------------------------
  const positionTip = useCallback((clientX: number, clientY: number) => {
    const wrap = wrapRef.current;
    const el = tipRef.current;
    if (!wrap || !el) return;
    const r = wrap.getBoundingClientRect();
    const tw = el.offsetWidth;
    const th = el.offsetHeight;
    let x = clientX - r.left + 14;
    let y = clientY - r.top + 14;
    if (x + tw > r.width) x = clientX - r.left - tw - 14;
    if (y + th > r.height) y = clientY - r.top - th - 14;
    x = Math.max(4, x);
    y = Math.max(4, y);
    el.style.left = x + "px";
    el.style.top = y + "px";
    el.style.opacity = "1";
  }, []);

  const onLeafMove = useCallback(
    (e: React.MouseEvent, leaf: AppLeaf, category: string) => {
      setTip((prev) =>
        prev && prev.leaf === leaf ? prev : { leaf, category }
      );
      positionTip(e.clientX, e.clientY);
    },
    [positionTip]
  );

  const hideTip = useCallback(() => {
    if (tipRef.current) tipRef.current.style.opacity = "0";
  }, []);

  // --- filters ---------------------------------------------------------------
  const optionDisabled = useCallback(
    (key: keyof Selection, value: string) =>
      !!data &&
      value !== "all" &&
      totalFor(data, { ...sel, [key]: value }) < data.minSample,
    [data, sel]
  );

  const onDownload = useCallback(async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);
    try {
      await downloadTreemapImage(cardRef.current);
    } catch (err) {
      console.error("Download image failed", err);
    } finally {
      setDownloading(false);
    }
  }, [downloading]);

  const subtitle = data
    ? `${data.meta.period} · ${data.meta.total_label} brands`
    : "";

  // --- leaf renderer ---------------------------------------------------------
  const renderLeaf = (l: PositionedLeaf, i: number) => {
    const fill = mode === "mom" ? momColor(l.leaf.momRel) : l.categoryColor;
    const label = fitLeafLabel(l.w, l.h);
    const onGrey =
      mode === "mom" &&
      l.leaf.momRel != null &&
      l.leaf.momRel >= -1 &&
      l.leaf.momRel <= 1;
    const textFill = onGrey ? "#1a1d24" : "#fff";
    const subText =
      mode === "mom" ? fmtRel(l.leaf.momRel) : l.leaf.value.toFixed(1) + "%";
    const clipId = `${uid}-lc${i}`;
    const style =
      doAnimate && !stacked
        ? {
            animation: `wf-treemap-cell-in 450ms ease-out ${Math.round(
              Math.max(0, 1 - l.x / width) * 1100
            )}ms both`,
          }
        : undefined;

    return (
      <Fragment key={i}>
        <clipPath id={clipId}>
          <rect x={l.x} y={l.y} width={l.w} height={l.h} />
        </clipPath>
        <g
          className={styles.leaf}
          clipPath={`url(#${clipId})`}
          style={style}
          data-tm-leaf=""
          onMouseMove={(e) => onLeafMove(e, l.leaf, l.categoryName)}
        >
          <rect
            className={styles.leafRect}
            x={l.x}
            y={l.y}
            width={l.w}
            height={l.h}
            rx={1.5}
            fill={fill}
          />
          {label.kind === "horizontal" && (
            <>
              <text
                className={styles.leafText}
                x={l.x + 4}
                y={l.y + label.topY}
                fontSize={label.nameFs}
                fontWeight={650}
                fill={textFill}
              >
                {fitHorizontalName(l.leaf.name, l.w, label.nameFs)}
              </text>
              {label.showSub && (
                <text
                  className={styles.leafText}
                  x={l.x + 4}
                  y={l.y + label.subY}
                  fontSize={label.subFs}
                  fill={textFill}
                  opacity={0.82}
                >
                  {subText}
                </text>
              )}
            </>
          )}
          {label.kind === "rotated" && (
            <>
              <text
                className={styles.leafText}
                transform={`translate(${l.x + label.nameFs + 1},${l.y + 4}) rotate(90)`}
                fontSize={label.nameFs}
                fontWeight={650}
                fill={textFill}
              >
                {fitRotatedName(l.leaf.name, l.h, label.nameFs)}
              </text>
              {label.showSub && (
                <text
                  className={styles.leafText}
                  transform={`translate(${l.x + label.nameFs + 1 + (label.nameFs - 1.5) + 3},${l.y + 4}) rotate(90)`}
                  fontSize={label.subFs}
                  fill={textFill}
                  opacity={0.82}
                >
                  {subText}
                </text>
              )}
            </>
          )}
        </g>
      </Fragment>
    );
  };

  // --- chart body ------------------------------------------------------------
  let chart: React.ReactNode;
  if (!layout) {
    chart = (
      <svg
        className={styles.svg}
        viewBox={`0 0 ${width} ${chooseUnifiedHeight(width)}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <text
          x={width / 2}
          y={chooseUnifiedHeight(width) / 2}
          fill="#5b616e"
          textAnchor="middle"
          fontSize={16}
        >
          No brands match this combination.
        </text>
      </svg>
    );
  } else if (layout.kind === "unified") {
    chart = (
      <svg
        className={styles.svg}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ fontFamily: "inherit" }}
      >
        {layout.cats.map((c, i) => {
          const header = fitCatHeader(c.name, c.w);
          const clipId = `${uid}-hc${i}`;
          return (
            <g key={`cat-${i}`} clipPath={`url(#${clipId})`}>
              <clipPath id={clipId}>
                <rect x={c.x} y={c.y} width={c.w} height={PAD_TOP} />
              </clipPath>
              {header.lines.map((ln, j) => (
                <text
                  key={j}
                  className={styles.catLabel}
                  x={c.x + 3}
                  y={c.y + ln.dy}
                  fontSize={header.fontSize}
                  fill="#1a1d24"
                >
                  {ln.text}
                </text>
              ))}
            </g>
          );
        })}
        {layout.leaves.map(renderLeaf)}
      </svg>
    );
  } else {
    chart = (
      <div>
        {layout.rows.map((row, ri) => (
          <div key={ri} className={styles.stackRow}>
            <p className={styles.stackHeader}>
              <span
                className={styles.stackSwatch}
                style={{
                  background: mode === "mom" ? "#c7ccd6" : row.color,
                }}
              />
              {row.name}
            </p>
            <svg
              className={styles.svg}
              viewBox={`0 0 ${layout.width} ${row.height}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ fontFamily: "inherit" }}
            >
              {row.leaves.map((l, i) => renderLeaf(l, ri * 1000 + i))}
            </svg>
          </div>
        ))}
      </div>
    );
  }

  // --- loading / error / compact -------------------------------------------
  if (loadError) {
    return (
      <div ref={cardRef} className={styles.root}>
        <div style={{ padding: 20, color: "#c0392b", fontSize: 13 }}>
          Could not load the chart.
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div ref={cardRef} className={styles.root}>
        {data ? (
          <div ref={wrapRef} className={styles.wrap} onMouseLeave={hideTip}>
            {chart}
            <Tooltip tip={tip} tipRef={tipRef} />
          </div>
        ) : (
          <div style={{ minHeight: 300 }} aria-hidden />
        )}
      </div>
    );
  }

  if (!data) {
    return (
      <div ref={cardRef} className={styles.root}>
        <div style={{ minHeight: 560 }} aria-hidden />
      </div>
    );
  }

  const modeChips =
    mode === "mom" ? (
      <div className={styles.lgLayer}>
        <span className={styles.chip}>
          <span className={styles.sw} style={{ background: "#1f9d4d" }} />
          Growing
        </span>
        <span className={styles.chip}>
          <span
            className={styles.sw}
            style={{ background: NEW_ENTRANT_COLOR }}
          />
          New entrant
        </span>
        <span className={styles.chip}>
          <span className={styles.sw} style={{ background: "#c7ccd6" }} />
          Flat
        </span>
        <span className={styles.chip}>
          <span className={styles.sw} style={{ background: "#c0392b" }} />
          Declining
        </span>
      </div>
    ) : (
      <div className={styles.lgLayer}>
        {data.categoryOrder.map((c) => (
          <span className={styles.chip} key={c}>
            <span
              className={styles.sw}
              style={{ background: data.categoryColor[c] }}
            />
            {c}
          </span>
        ))}
      </div>
    );

  return (
    <div
      ref={cardRef}
      className={`${styles.root}${downloading ? " " + styles.exporting : ""}`}
    >
      <div>
        <h1 className={styles.title} data-tm-title>
          {data.meta.title}
        </h1>
        <div className={styles.sub}>{subtitle}</div>
      </div>

      <div className={styles.controls}>
        <div className={styles.ctrl}>
          <label htmlFor={`${uid}-fGmv`}>Annual GMV</label>
          <select
            id={`${uid}-fGmv`}
            data-tm-select
            className={styles.select}
            value={sel.gmv}
            onChange={(e) => setSel((s) => ({ ...s, gmv: e.target.value }))}
          >
            <option value="all">All</option>
            {GMV_OPTS.map(([v, l]) => (
              <option key={v} value={v} disabled={optionDisabled("gmv", v)}>
                {l}
                {optionDisabled("gmv", v) ? " (n/a)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.ctrl}>
          <label htmlFor={`${uid}-fGrowth`}>Growth</label>
          <select
            id={`${uid}-fGrowth`}
            data-tm-select
            className={styles.select}
            value={sel.growth}
            onChange={(e) => setSel((s) => ({ ...s, growth: e.target.value }))}
          >
            <option value="all">All</option>
            {GROWTH_OPTS.map(([v, l]) => (
              <option key={v} value={v} disabled={optionDisabled("growth", v)}>
                {l}
                {optionDisabled("growth", v) ? " (n/a)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.ctrl}>
          <label htmlFor={`${uid}-fInd`}>Industry</label>
          <select
            id={`${uid}-fInd`}
            data-tm-select
            className={styles.select}
            value={sel.ind}
            onChange={(e) => setSel((s) => ({ ...s, ind: e.target.value }))}
          >
            <option value="all">All</option>
            {data.industries.map((i) => (
              <option
                key={i.key}
                value={i.key}
                disabled={optionDisabled("ind", i.key)}
              >
                {i.label}
                {optionDisabled("ind", i.key) ? " (n/a)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.ctrl}>
          <span className={styles.ctrlLabel} aria-hidden>
            &nbsp;
          </span>
          <button
            type="button"
            className={styles.reset}
            onClick={() => setSel({ gmv: "all", growth: "all", ind: "all" })}
          >
            Reset
          </button>
        </div>
        <div className={styles.ctrl}>
          <span className={styles.ctrlLabel}>Color by</span>
          <div className={styles.seg}>
            <button
              type="button"
              className={mode === "cat" ? styles.active : ""}
              onClick={() => setMode("cat")}
            >
              Adoption
            </button>
            <button
              type="button"
              className={mode === "mom" ? styles.active : ""}
              onClick={() => setMode("mom")}
            >
              Momentum (MoM)
            </button>
          </div>
        </div>
      </div>

      <div className={styles.legend}>
        {modeChips}
        {/* Hidden sizing layer: keep the taller (category) set mounted so the
            legend row height never changes when toggling. */}
        <div
          className={styles.lgLayer}
          aria-hidden
          style={{ visibility: "hidden" }}
        >
          {data.categoryOrder.map((c) => (
            <span className={styles.chip} key={c}>
              <span className={styles.sw} />
              {c}
            </span>
          ))}
        </div>
      </div>

      {belowSample && (
        <div className={styles.warn}>
          This combination has too few brands to show reliably and is not
          displayed.
        </div>
      )}

      <div ref={wrapRef} className={styles.wrap} onMouseLeave={hideTip}>
        {chart}
        <Tooltip tip={tip} tipRef={tipRef} />
      </div>

      <footer className={styles.footer}>
        <div className={styles.left}>
          <span className={styles.callout}>
            <a href={data.meta.callout_url} target="_blank" rel="noopener">
              {data.meta.callout}
            </a>
          </span>
          <span
            className={styles.note}
            // note_html is authored by our own build tool. It is raw HTML, so
            // treemapDataSchema (./types) validates it at the boundary: a
            // refresh carrying <script>, an on*= handler, a javascript:/data:
            // URL or any tag outside the small inline allow-list fails to parse
            // rather than reaching this sink.
            dangerouslySetInnerHTML={{ __html: data.meta.note_html }}
          />{" "}
          ·{" "}
          <button
            type="button"
            className={styles.dlPhoto}
            data-tm-download
            onClick={onDownload}
          >
            {downloading ? "Preparing…" : "Download image"}
          </button>
        </div>
        <div className={styles.logo}>
          <a
            href={data.meta.callout_url}
            target="_blank"
            rel="noopener"
            aria-label="Wayflyer data"
          >
            <WfLogo />
          </a>
        </div>
      </footer>
    </div>
  );
}

function Tooltip({
  tip,
  tipRef,
}: {
  tip: TooltipState | null;
  tipRef: React.RefObject<HTMLDivElement | null>;
}) {
  const isNew = tip?.leaf.momRel == null;
  const cls =
    tip && tip.leaf.momRel != null
      ? tip.leaf.momRel > 0
        ? styles.pos
        : tip.leaf.momRel < 0
          ? styles.neg
          : styles.zero
      : styles.zero;
  return (
    <div ref={tipRef} className={styles.tip}>
      {tip && (
        <>
          <div className={styles.tipName}>{tip.leaf.name}</div>
          <div className={styles.tipCat}>{tip.category}</div>
          <div className={styles.tipRow}>
            <span className={styles.tipK}>Adoption</span>
            <span>{tip.leaf.value.toFixed(1)}%</span>
          </div>
          <div className={styles.tipRow}>
            <span className={styles.tipK}>MoM change</span>
            {isNew ? (
              <span style={{ color: NEW_ENTRANT_COLOR, fontWeight: 600 }}>
                New entrant
              </span>
            ) : (
              <span className={cls}>{fmtRel(tip.leaf.momRel)}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
